import EmailArchiveImport from '@/components/EmailArchiveImport';
import EmailCasePanel from "@/components/EmailCasePanel";
import EmailContentViewer from "@/components/EmailContentViewer";
import { userError } from "@/lib/userError";
import React, { useRef, useEffect, useState } from "react";
import { Mail, ExternalLink, Sparkles, RefreshCw, CheckCircle2 } from "lucide-react";
import { analyzeEmails, getEmails, getEmailSummary, syncGmail, draftReply, setEmailCompletion, setEmailReplyState, sendEmail, setEmailAutomation, reviewEmail } from "@/lib/apiClient";
import { useData, useDataRefresh } from "@/lib/useData";
import { ContentSearch } from "@/components/ContentSearch";
import { matchesContent } from "@/lib/contentSearch";
import { PageHeader, LoadingCards, ErrorState, EmptyState, RefreshNotice } from "@/components/Common";
import { StatusBadge } from "@/components/StatusBadge";
import { ApprovalButton } from "@/components/ApprovalButton";
import { fiRelative } from "@/lib/constants";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { safeExternalUrl } from "@/lib/safeUrl";
import { DataFreshnessNotice } from "@/components/DataFreshnessNotice";
import {getEmailItem} from "@/lib/apiClient";
import {useLinkedRecord} from "@/lib/useLinkedRecord";
import {schedulerRecent, useFreshnessClock} from "@/lib/sourceFreshness";
import {LinkedRecord} from "@/components/LinkedRecord";
import {useMailboxCounts} from "@/lib/useMailboxCounts";

const TABS = [
  { id: "all", label: "Kaikki" },
  { id: "needs_review", label: "Tarkistettavat" },
  { id: "urgent", label: "Kiireelliset" },
  { id: "reply_needed", label: "Vastausta vaativat" },
  { id: "invoice_info", label: "Laskutus" },
  { id: "waiting", label: "Odottaa muita" },
  { id: "handled", label: "Hoidetut" },
];

const CATEGORY_TONE = {
  urgent: "border-rose-500/35 bg-rose-500/[0.04]",
  reply_needed: "border-sky-500/35 bg-sky-500/[0.04]",
  invoice_info: "border-amber-500/35 bg-amber-500/[0.04]",
};

const CATEGORY_LABEL = {
  urgent: { label: "Kiireellinen", tone: "border-rose-500/30 bg-rose-500/15 text-rose-300" },
  reply_needed: { label: "Vastausta vaativa", tone: "border-sky-500/30 bg-sky-500/15 text-sky-300" },
  invoice_info: { label: "Laskutus", tone: "border-amber-500/30 bg-amber-500/15 text-amber-300" },
};

const EMPTY_COPY = {
  handled: {title:"Ei vielä hoidettuja viestejä",hint:"Tehdyksi merkityt viestit säilyvät täällä."},
  waiting: {title:"Ei muita odottavia asioita",hint:"Toisen osapuolen vastausta odottavat asiat näkyvät täällä."},
  needs_review: { title: "Ei tarkistettavia haetuissa viesteissä", hint: "Tämä ei vielä varmista haun kattavuutta. Tarkista yllä näkyvä haun tila." },
  all: {
    title: "Ei vielä sähköposteja",
    hint: "Paina Hae uudet. Jos sähköpostiyhteyttä ei ole, yhdistä se asetuksissa.",
  },
  urgent: {
    title: "Ei kiireellisiä viestejä",
    hint: "Tähän luokkaan ei ole merkitty viestejä. Tarkista myös keskeneräisten viestien jono ja haun kattavuus.",
  },
  reply_needed: {
    title: "Ei vastausta odottavia viestejä",
    hint: "Tähän luokkaan ei ole merkitty viestejä. Tarkista myös keskeneräisten viestien jono ja haun kattavuus.",
  },
  invoice_info: {
    title: "Ei laskutusviestejä",
    hint: "Analyysi ei ole löytänyt tällä hetkellä laskutukseen liittyviä viestejä.",
  },
};

export const EmailCard = ({ email, onChanged, initialExpanded = false }) => {
  const [expanded, setExpanded] = useState(initialExpanded);
  const remoteDraft = typeof email.draft_reply === "string" ? email.draft_reply : (email.suggested_reply || "");
  const sourceKey = JSON.stringify([typeof email.draft_reply === "string" ? "saved" : "suggested", remoteDraft]);
  const [editor, setEditor] = useState(() => ({ id: email.id, text: remoteDraft, base: remoteDraft, sourceKey }));
  const draft = editor.id === email.id ? editor.text : remoteDraft;
  const dirty = editor.id === email.id && editor.text !== editor.base;
  const conflict = dirty && editor.sourceKey !== sourceKey && draft !== remoteDraft;
  const unsaved = typeof email.draft_reply !== "string" || draft !== email.draft_reply;
  const [updatingReplyState, setUpdatingReplyState] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const sender = email.sender_name || email.sender || "Tuntematon lähettäjä";
  const sourceLink = safeExternalUrl(email.source_link);
  const completed = email.work_state === "done" || ["Valmis","Ei vaadi vastausta","Lähetetty"].includes(email.reply_status);
  const dismissed = email.reply_status === "Ei vaadi vastausta";
  const category = completed ? {label:"Hoidettu",tone:"border-emerald-500/30 text-emerald-300"} : email.work_state === "waiting" ? {label:"Odottaa muita",tone:"border-border"} : CATEGORY_LABEL[email.category];
  const canReply = !completed && !dismissed && (email.category === "reply_needed" || Boolean(email.draft_reply || email.suggested_reply));
  const summary = Array.isArray(email.summary)
    ? email.summary.filter(Boolean)
    : (email.snippet ? [email.snippet] : []);

  useEffect(() => {
    setEditor(current => {
      if (current.id === email.id && current.sourceKey === sourceKey) return current;
      if (current.id !== email.id || current.text === current.base || current.text === remoteDraft) {
        return {id: email.id, text: remoteDraft, base: remoteDraft, sourceKey};
      }
      return current; // Preserve unsaved typing until the user chooses a version.
    });
  }, [email.id, sourceKey, remoteDraft, editor.text]);

  const saveDraft = async () => {
    if (savingDraft || conflict || !draft.trim()) return;
    setSavingDraft(true);
    try {
      await draftReply(email.id, draft);
      toast.success("Vastausluonnos tallennettu (ei lähetetty).");
      await onChanged();
    } catch (requestError) {
      toast.error(userError(requestError, "Vastausluonnoksen tallentaminen epäonnistui."));
    } finally {
      setSavingDraft(false);
    }
  };

  const setReplyState = async (needsReply) => {
    setUpdatingReplyState(true);
    try {
      await setEmailReplyState(email.id, needsReply);
      toast.success(needsReply ? "Viesti palautettiin vastausjonoon." : "Viesti poistettiin vastausjonosta.");
      await onChanged();
    } catch (requestError) {
      toast.error(userError(requestError, "Vastaustilan päivittäminen epäonnistui."));
    } finally {
      setUpdatingReplyState(false);
    }
  };

  return (
    <div className={cn("rounded-2xl border bg-card p-4 fade-up", CATEGORY_TONE[email.category] || "border-border")} data-testid={`email-${email.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium text-foreground">{email.subject}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{sender} · {fiRelative(email.received_at)}</p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          {category && <span className={cn("rounded-full border px-2 py-0.5 text-[10px] font-semibold", category.tone)}>{category.label}</span>}
          {canReply && email.reply_status && <StatusBadge status={email.reply_status} />}
        </div>
      </div>

      {email.task_suggestion_id&&<a href={`/tasks?suggestion=${encodeURIComponent(email.task_suggestion_id)}`} className="mt-2 inline-flex min-h-11 items-center gap-2 text-xs text-primary"><Sparkles className="h-3.5 w-3.5"/>Tehtäväehdotus tarkistettavaksi</a>}
      {!["analyzed", "reviewed"].includes(email.analysis_state) && <details className="mt-2 text-xs text-amber-400"><summary className="min-h-8 cursor-pointer">{email.analysis_state === "uncertain" ? "Epävarma luokittelu" : "Tarkistamaton"}</summary><button type="button" className="min-h-11 text-primary disabled:opacity-50" disabled={updatingReplyState} onClick={async () => {
        setUpdatingReplyState(true);
        try { await reviewEmail(email.id); await onChanged(); toast.success("Merkitty tarkistetuksi. Mitään ei lähetetty."); }
        catch (error) { toast.error(userError(error,"Tarkistusta ei voitu tallentaa.")); }
        finally { setUpdatingReplyState(false); }
      }}>Olen tarkistanut tämän viestin</button></details>}
      <ul className="mt-3 space-y-1">
        {summary.map((s, i) => (
          <li key={i} className="text-sm text-muted-foreground flex gap-2">
            <span className="text-primary mt-1">•</span><span>{s}</span>
          </li>
        ))}
      </ul>

      <EmailCasePanel key={email.id} email={email} onChanged={onChanged}/>
      <EmailContentViewer key={JSON.stringify([email.id,email.body_digest,email.snippet])} email={email} onChanged={onChanged}/>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button type="button" disabled={updatingReplyState} className="min-h-11 text-sm text-primary disabled:opacity-50" onClick={async()=>{
          setUpdatingReplyState(true);
          try {await setEmailCompletion(email.id,!completed);await onChanged();toast.success(completed?"Asia palautettiin käsittelyyn.":"Asia siirrettiin hoidettuihin.");}
          catch(error){toast.error(userError(error,"Asian tilaa ei voitu tallentaa."));}
          finally{setUpdatingReplyState(false);}
        }}>{updatingReplyState?"Tallennetaan…":completed?"Palauta käsittelyyn":"Merkitse tehdyksi"}</button>
        {sourceLink && (
          <a href={sourceLink} target="_blank" rel="noopener noreferrer" data-testid={`email-source-${email.id}`}
             className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
            <ExternalLink className="h-3 w-3" /> Lähde
          </a>
        )}
        {canReply && (
          <button type="button" data-testid={`email-toggle-${email.id}`} onClick={() => setExpanded((v) => !v)} aria-expanded={expanded}
                  className="inline-flex items-center gap-1 text-xs text-sky-400 hover:underline">
            <Sparkles className="h-3 w-3" /> {expanded ? "Piilota" : typeof email.draft_reply === "string" ? "Vastausluonnos" : "Ehdotettu vastaus"}
          </button>
        )}
        {canReply && (
          <button type="button" onClick={() => setReplyState(false)} disabled={updatingReplyState}
                  className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50">
            <CheckCircle2 className="h-3 w-3" /> {updatingReplyState ? "Päivitetään…" : "Ei vaadi vastausta"}
          </button>
        )}
        {dismissed && (
          <button type="button" onClick={() => setReplyState(true)} disabled={updatingReplyState}
                  className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50">
            <RefreshCw className="h-3 w-3" /> {updatingReplyState ? "Päivitetään…" : "Palauta vastausjonoon"}
          </button>
        )}
      </div>

      {expanded && canReply && (
        <div className="mt-3 space-y-3 border-t border-border pt-3">
          <Textarea
            data-testid={`email-draft-${email.id}`}
            value={draft}
            aria-label={`Vastausluonnos: ${email.subject}`}
            disabled={savingDraft}
            onChange={(e) => { const text = e.target.value; setEditor(current => ({...current, text})); }}
            rows={5}
            className="text-sm rounded-xl"
          />
          {conflict && <div role="status" className="space-y-2 rounded-xl border border-amber-500/40 p-3 text-sm">
            <p>Viestin vastausversio päivittyi. Oma tekstisi säilytettiin. Valitse versio ennen tallentamista.</p>
            <details><summary className="min-h-11 cursor-pointer text-primary">Näytä uusi versio</summary><p className="whitespace-pre-wrap break-words">{remoteDraft || "Uusi versio on tyhjä."}</p></details>
            <div className="flex flex-wrap gap-2">
              <button type="button" disabled={savingDraft} className="min-h-11 rounded-full border border-border px-3" onClick={() => setEditor({id:email.id,text:draft,base:remoteDraft,sourceKey})}>Säilytä oma tekstini</button>
              <button type="button" disabled={savingDraft} className="min-h-11 rounded-full border border-border px-3" onClick={() => setEditor({id:email.id,text:remoteDraft,base:remoteDraft,sourceKey})}>Korvaa oma tekstini uudella versiolla</button>
            </div>
          </div>}
          {unsaved && <p className="text-xs text-muted-foreground">Tallenna tämä teksti luonnokseksi ennen lähetyksen hyväksymistä. Tallentaminen ei lähetä viestiä.</p>}
          <div className="flex flex-wrap gap-2">
            <button type="button" data-testid={`email-savedraft-${email.id}`} onClick={saveDraft} disabled={savingDraft || conflict || !draft.trim()}
                    className="rounded-full border border-border px-4 py-2 text-sm font-semibold text-foreground hover:bg-muted disabled:opacity-50">
              {savingDraft ? "Tallennetaan…" : "Tallenna luonnos"}
            </button>
            <ApprovalButton
              testid={`email-send-${email.id}`}
              action="send_email"
              resourceId={email.id}
              triggerLabel="Hyväksy & lähetä"
              disabled={savingDraft || conflict || unsaved || !draft.trim()}
              confirmLabel="Lähetä"
              title="Vahvista sähköpostin lähetys"
              description={`Vastaus lähetetään vastaanottajalle: ${sender}.\n\nAssistentti ei koskaan lähetä automaattisesti — tämä vaatii hyväksyntäsi.`}
              perform={async (token) => {
                if (savingDraft || conflict || unsaved || !draft.trim()) throw new Error("Tallenna ja tarkista vastausluonnos ennen lähettämistä.");
                await sendEmail(email.id, token);
                toast.success("Vastaus lähetetty.");
                onChanged();
              }}
            />
          </div>
          <p className="text-[11px] text-muted-foreground">Assistentti ei lähetä viestejä automaattisesti.</p>
        </div>
      )}
    </div>
  );
};

export default function Inbox() {
  const mailbox = useMailboxCounts();
  const freshnessNow = useFreshnessClock();
  const selection=useLinkedRecord("email",getEmailItem,"emails");
  const [tab, setTab] = useState(() => {
    const category = new URLSearchParams(window.location.search).get("category");
    return TABS.some(item => item.id === category) ? category : "all";
  });
  const [query, setQuery] = useState("");
  const tabRef = useRef(tab);
  tabRef.current = tab;
  const olderRunRef = useRef(0);
  const olderBusyRef = useRef(false);
  const [summary, setSummary] = useState(null);
  const [working, setWorking] = useState("");
  const [olderEmails, setOlderEmails] = useState([]);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const { data, loading, error, refetch, refreshing, updatedAt } = useData(() => getEmails(tab), [tab]);
  const loadedEmails = [...(data || []), ...olderEmails];
  const visibleEmails = loadedEmails.filter((email) => email.id!==selection.data?.id && matchesContent(query, [email.subject, email.sender, email.sender_email, email.sender_name]));
  const emptyCopy = EMPTY_COPY[tab] || EMPTY_COPY.all;
  const mailboxAge=Date.now()-Date.parse(summary?.coverage?.last_sync);

  useEffect(() => {
    getEmailSummary().then(setSummary).catch(() => {});
  }, []);

  useEffect(() => {
    olderRunRef.current += 1;
    olderBusyRef.current = false;
    setLoadingOlder(false);
    setOlderEmails([]);
    setHasMore(true);
  }, [tab]);

  const refreshVisible = async (refreshSelection = true) => {
    olderRunRef.current += 1;
    olderBusyRef.current = false;
    setLoadingOlder(false);
    setOlderEmails([]);
    setHasMore(true);
    await refetch();
    if(refreshSelection && selection.present)await selection.refetch();
    try { setSummary(await getEmailSummary()); } catch { setSummary(null); }
  };
  // Reset paginated rows too; otherwise an older cached page can retain a draft.
  useDataRefresh(()=>refreshVisible(false), ["emails"]);

  const loadOlder = async () => {
    if (olderBusyRef.current) return;
    olderBusyRef.current = true;
    const activeTab = tab;
    const run = ++olderRunRef.current;
    setLoadingOlder(true);
    try {
      const next = await getEmails(tab, 40, loadedEmails.length);
      if (run !== olderRunRef.current || activeTab !== tabRef.current) return;
      setOlderEmails((current) => [...current, ...next]);
      setHasMore(next.length === 40);
    } catch {
      toast.error("Vanhempien viestien lataaminen epäonnistui.");
    } finally {
      if (run === olderRunRef.current) { olderBusyRef.current = false; setLoadingOlder(false); }
    }
  };

  const refreshMail = async () => {
    setWorking("sync");
    let syncResult;
    try {
      syncResult = await syncGmail();
      await refreshVisible();
      toast.success(syncResult.status==='queued'?'Viestien tarkistus käynnistettiin. Uudet viestit ilmestyvät automaattisesti.':`${syncResult.synced} viestiä päivitetty. Käsittelemättömät näkyvät Tarkistettavat-jonossa.`);
      setWorking("");
      return;
    } catch (requestError) {
      toast.error(userError(requestError, "Sähköpostien päivitys epäonnistui."));
      setWorking("");
      return;
    }

  };

  const runAnalysis = async () => {
    setWorking("analyze");
    try {
      const result = await analyzeEmails();
      await refreshVisible();
      setSummary(result);
      toast.success(result.provider === "local_rules" ? "Vain paikallinen varaluokittelu onnistui. Viestit jäivät tarkistettaviksi." : `${result.analyzed} viestiä käsitelty. Tarkista myös keskeneräisten jono. Mitään ei lähetetty.`);
    } catch (requestError) {
      toast.error(userError(requestError, "Sähköpostianalyysi epäonnistui."));
    } finally {
      setWorking("");
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <PageHeader icon={Mail} title="Sähköpostit" />
      <p role="status" className="text-xs text-muted-foreground">{mailbox.error ? "Viestimäärien päivitys epäonnistui. Viestit näkyvät alla." : summary?.coverage?.arrival?.ready ? 'Uudet Gmail-viestit saapuvat automaattisesti.' : summary?.coverage?.arrival?.connected ? 'Gmail-yhteys on tauolla, valmisteltavana tai vaatii huomiota. Tarkista asetukset.' : !summary?.coverage?.sync_complete || summary?.coverage?.sync_error || summary?.coverage?.sync_stale || !Number.isFinite(mailboxAge) || mailboxAge<0 || mailboxAge>10800000 ? "Haun ajantasaisuus on varmistamatta." : `Viimeisin haku ${fiRelative(summary.coverage.last_sync)}.`}</p>
      <details className="order-last mt-4 rounded-2xl border border-border p-3" data-testid="mail-tools">
      <summary className="min-h-11 cursor-pointer text-sm font-medium">Postin asetukset ja toiminnot</summary>
      {!summary?.coverage?.arrival&&<DataFreshnessNotice key={summary?.coverage?.last_sync || "unknown"} integrationId="int-gmail" sourceLabel="Sähköposti" coverageComplete={summary?.coverage?.sync_complete === true} />}
      {summary?.coverage?.arrival&&<p className="my-2 text-xs text-muted-foreground">Oman Gmail-yhteyden aloituksessa tuodaan enintään 100 uusinta viestiä. Tämä ei ole koko vanhan arkiston tuonti. <a className="text-primary underline" href="/settings">Hallitse Gmail-yhteyttä asetuksissa</a></p>}
      <section className="mb-4 rounded-2xl border border-amber-500/30 p-4 text-sm" aria-label="Sähköpostien käsittelytilanne">
        <p className="font-semibold">{summary?.coverage ? `${summary.coverage.checked} / ${summary.coverage.total} haettua viestiä tarkistettu` : "Sähköpostien käsittelytilannetta ei ole varmistettu"}</p>
        {Boolean(summary?.coverage?.needs_review) && <button type="button" className="min-h-11 text-primary" onClick={() => setTab("needs_review")}>{summary.coverage.needs_review} viestiä odottaa tarkistusta</button>}
        {(!summary?.coverage?.sync_complete || summary?.coverage?.sync_error) && <p className="mt-2 text-muted-foreground">Haun kattavuutta ei ole varmistettu. Postilaatikossa voi olla lisää viestejä, vaikka jokin luokka olisi tyhjä.</p>}
        {Number.isInteger(summary?.coverage?.latest_import_count) && <p className="mt-2 text-xs text-muted-foreground">Viimeisin tuonti toi {summary.coverage.latest_import_count} viestiä. Tämä ei ole postilaatikon kokonaismäärä. Alla oleva lisälataus näyttää vain appiin jo tallennettuja viestejä.</p>}
        <EmailArchiveImport onChanged={()=>refreshVisible()}/>
        {summary?.coverage?.last_sync && <p className="mt-2 text-xs text-muted-foreground">Viimeisin onnistunut haku: {new Date(summary.coverage.last_sync).toLocaleString("fi-FI")}</p>}
        {summary?.coverage?.sync_stale && <p className="mt-2 text-amber-400">Sähköpostitietojen tuoreutta ei ole varmistettu. Haku pitää uusia.</p>}
        {summary?.provider === "local_rules" && <p className="mt-2 text-amber-400">Paikallinen varatila käytössä. Tekoälyanalyysi ei onnistunut, joten luokittelu vaatii tarkistuksen.</p>}
        {!summary?.coverage?.arrival&&<button type="button" className="mt-2 min-h-11 text-primary disabled:opacity-50" disabled={Boolean(working) || !summary?.coverage} onClick={async () => {
          setWorking("automation");
          try { const coverage = await setEmailAutomation(!summary.coverage.automation_enabled); setSummary(current => ({ ...current, coverage })); }
          catch (error) { toast.error(userError(error, "Automaation asetusta ei voitu muuttaa.")); }
          finally { setWorking(""); }
        }}>{summary?.coverage?.automation_enabled ? "Poista sähköpostiautomaatio käytöstä" : "Ota käyttöön haku 3 h välein ja luokittelu (käyttää krediittejä)"}</button>}
        {summary?.coverage?.automation_enabled && !summary.coverage.scheduler_configured && <p className="mt-2 text-amber-400">Tausta-ajo on sallittu, mutta ulkoista heräteajastinta ei ole määritetty. Palvelimen nukkuessa ajo voi viivästyä.</p>}
        {summary?.coverage?.automation_enabled && summary.coverage.scheduler_configured && !schedulerRecent(summary.coverage, freshnessNow) && <p className="mt-2 text-amber-400">Ulkoisen ajastimen tuoretta toimintaa ei ole vahvistettu. Tarkista ajastimen yhteys.</p>}
        {summary?.coverage?.automation_error && <p className="mt-2 text-amber-400">Edellinen automaattinen käsittely epäonnistui. Uudelleenyritys on ajastettu; keskeneräiset viestit säilyvät jonossa.</p>}
      </section>
      <section className="mb-4 rounded-2xl border border-primary/20 bg-primary/5 p-4">
        <div className="flex items-start gap-3">
          <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-foreground">Viimeisimmän käsittelyerän yhteenveto</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {summary?.overview || "Päivitä viestit ilman tekoälyanalyysiä. Voit analysoida ne erikseen, kun käyttörajaa on jäljellä."}
            </p>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <button type="button" onClick={refreshMail} disabled={Boolean(working)} className="inline-flex items-center gap-2 rounded-full border border-border px-3 py-2 text-xs font-semibold disabled:opacity-50">
            <RefreshCw className={cn("h-3.5 w-3.5", ["sync", "analyze-after-sync"].includes(working) && "animate-spin")} />
            {working === "sync" ? "Haetaan…" : working === "analyze-after-sync" ? "Analysoidaan…" : "Päivitä viestit"}
          </button>
          <button type="button" onClick={runAnalysis} disabled={Boolean(working)} className="inline-flex items-center gap-2 rounded-full bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground disabled:opacity-50">
            <Sparkles className="h-3.5 w-3.5" />
            {working === "analyze" ? "Analysoidaan…" : "Analysoi (käyttää krediittejä)"}
          </button>
        </div>
      </section>
      </details>
      <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap" data-testid="inbox-tabs">
        {TABS.map((t) => (
          <button
            type="button"
            key={t.id}
            data-testid={`tab-${t.id}`}
            onClick={() => setTab(t.id)}
            aria-pressed={tab === t.id}
            aria-label={`${t.label}${mailbox.data?.categories?.[t.id] ? `, ${mailbox.data.categories[t.id].total} viestiä, ${mailbox.data.categories[t.id].new===null?"uusien määrä tuntematon":`${mailbox.data.categories[t.id].new} uutta`}` : ", määrä tuntematon"}`}
            className={cn(
              "min-h-11 rounded-2xl px-3 py-2 text-left text-sm font-medium transition-colors",
              tab === t.id ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"
            )}
          >
            <span>{t.label}</span> <span className="ml-1 font-mono">{mailbox.data?.categories?.[t.id]?.total ?? "—"}</span>
            {mailbox.data?.categories?.[t.id]?.new != null && <span className="ml-2 inline-block rounded-full bg-background/20 px-2 py-0.5 text-xs">{mailbox.data.categories[t.id].new} uutta</span>}
          </button>
        ))}
      </div>

      <LinkedRecord selection={selection} label="viesti" path="/inbox">
        {selection.data && <EmailCard key={selection.data.id} email={selection.data} initialExpanded onChanged={refreshVisible}/>}
      </LinkedRecord>
      <ContentSearch value={query} onChange={setQuery} label="Etsi lähettäjällä tai aiheella" />
      <RefreshNotice refreshing={refreshing} error={data ? error : null} updatedAt={updatedAt} onRetry={refetch} />
      {loading ? <LoadingCards /> : error && !data ? <ErrorState error={error} onRetry={refetch} /> :
        visibleEmails.length === 0 ? <EmptyState icon={Mail} title={query ? "Ei hakutuloksia ladatuissa viesteissä" : selection.data ? "Ei muita viestejä ladatussa listassa" : emptyCopy.title} hint={query ? "Kokeile toista hakusanaa. Haku ei kata vielä lataamattomia viestejä." : emptyCopy.hint} /> : (
          <div className="space-y-3">
            {visibleEmails.map((e) => <EmailCard key={e.id} email={e} onChanged={refreshVisible} />)}
            {hasMore && (data || []).length === 40 && (
              <button type="button" onClick={loadOlder} disabled={loadingOlder} className="w-full rounded-full border border-border px-4 py-2.5 text-sm font-semibold text-muted-foreground disabled:opacity-50">
                {loadingOlder ? "Ladataan…" : "Näytä lisää tallennettuja viestejä"}
              </button>
            )}
          </div>
        )}
    </div>
  );
}
